/**
 * Real market microstructure for the Markets page, straight from the live
 * feed: the 10-level order book (MBP) and the streaming trade tape.
 * Nothing here is simulated — with no data the cards say so.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Card } from '../../components/UI';
import type { DepthLevel, LiveState, LiveTrade } from '../../lib/liveFeed';

const mono: CSSProperties = { fontFamily: 'var(--tk-mono)', fontVariantNumeric: 'tabular-nums' };
const fmt = (n: number | null | undefined, d = 2) =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en-PK', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-PK'));

const Head = ({ title, right }: { title: string; right?: string }) => (
  <div style={{ padding: '18px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h3>
    {right ? <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>{right}</span> : null}
  </div>
);

const ColHead = ({ cols, labels }: { cols: string; labels: [string, 'left' | 'right'][] }) => (
  <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '0 20px 8px', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--tk-ink-3)', ...mono }}>
    {labels.map(([l, a]) => (
      <span key={l} style={{ textAlign: a }}>
        {l}
      </span>
    ))}
  </div>
);

// ------------------------------------------------------------- order book ---

export const OrderBookCard = ({ live, marketOpen }: { live: LiveState; marketOpen: boolean }) => {
  const mbp = live.mbp;
  const maxQty = useMemo(() => (mbp ? Math.max(1, ...mbp.bids.map((l) => l.qty), ...mbp.asks.map((l) => l.qty)) : 1), [mbp]);
  const bidTotal = mbp ? mbp.bids.reduce((s, l) => s + l.qty, 0) : 0;
  const askTotal = mbp ? mbp.asks.reduce((s, l) => s + l.qty, 0) : 0;
  const sum = bidTotal + askTotal;
  const bidShare = sum ? (bidTotal / sum) * 100 : 50;
  const best = mbp && mbp.bids[0] && mbp.asks[0] ? { bid: mbp.bids[0].price, ask: mbp.asks[0].price } : null;

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <Head title="Order book" right={mbp ? `${mbp.bids.length + mbp.asks.length} levels` : undefined} />
      {!mbp ? (
        <div style={{ padding: '6px 20px 22px', fontSize: 12.5, color: 'var(--tk-ink-3)' }}>{marketOpen ? 'Waiting for the book…' : 'The book is published during market hours.'}</div>
      ) : (
        <>
          <ColHead cols="1fr 1fr 1fr" labels={[['Price', 'left'], ['Shares', 'right'], ['Orders', 'right']]} />
          <div style={{ display: 'flex', flexDirection: 'column-reverse' }}>
            {mbp.asks.map((l, i) => (
              <Level key={`a-${l.price}-${i}`} level={l} side="ask" maxQty={maxQty} />
            ))}
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 20px', margin: '4px 0', background: 'var(--tk-surface-2)', borderTop: '1px solid var(--tk-line-2)', borderBottom: '1px solid var(--tk-line-2)' }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', ...mono }}>{live.l1?.last ? fmt(live.l1.last) : '—'}</span>
            <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>{best ? `spread ${fmt(best.ask - best.bid)}` : ''}</span>
          </div>
          <div>
            {mbp.bids.map((l, i) => (
              <Level key={`b-${l.price}-${i}`} level={l} side="bid" maxQty={maxQty} />
            ))}
          </div>
          <div style={{ padding: '12px 20px 16px' }} title={`Bids ${fmtInt(bidTotal)} sh · asks ${fmtInt(askTotal)} sh across the visible levels`}>
            <div style={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', background: 'var(--tk-surface-2)' }}>
              <div style={{ width: `${bidShare}%`, background: 'var(--tk-up)', transition: 'width 0.5s var(--tk-ease)' }} />
              <div style={{ flex: 1, background: 'var(--tk-down)' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, ...mono }}>
              <span style={{ color: 'var(--tk-up)' }}>{bidShare.toFixed(0)}% bids</span>
              <span style={{ color: 'var(--tk-down)' }}>{(100 - bidShare).toFixed(0)}% asks</span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

const Level = ({ level, side, maxQty }: { level: DepthLevel; side: 'bid' | 'ask'; maxQty: number }) => (
  <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '4px 20px', fontSize: 12.5, ...mono }}>
    <span
      style={{ position: 'absolute', top: 1, bottom: 1, right: 0, width: `${Math.min(100, (level.qty / maxQty) * 100)}%`, background: side === 'bid' ? 'var(--tk-up-soft)' : 'var(--tk-down-soft)', transition: 'width 0.4s var(--tk-ease)' }}
    />
    <span style={{ position: 'relative', fontWeight: 600, color: side === 'bid' ? 'var(--tk-up)' : 'var(--tk-down)' }}>{fmt(level.price)}</span>
    <span style={{ position: 'relative', textAlign: 'right' }}>{fmtInt(level.qty)}</span>
    <span style={{ position: 'relative', textAlign: 'right', color: 'var(--tk-ink-3)' }}>{level.orders}</span>
  </div>
);

// ------------------------------------------------------------------- tape ---

export const TapeCard = ({ live, marketOpen }: { live: LiveState; marketOpen: boolean }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const rows = live.trades.slice(0, 60);
  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <Head title="Trades" right={rows.length ? `${fmtInt(live.l1?.trades)} today` : undefined} />
      {rows.length === 0 ? (
        <div style={{ padding: '6px 20px 22px', fontSize: 12.5, color: 'var(--tk-ink-3)' }}>{!live.bootstrapped ? 'Loading…' : marketOpen ? 'No trades yet today.' : 'No trades in the current session.'}</div>
      ) : (
        <>
          <ColHead cols="1fr 1fr 1fr" labels={[['Time', 'left'], ['Price', 'right'], ['Shares', 'right']]} />
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {rows.map((t, i) => (
              <TapeRow key={`${t.time}|${t.price}|${t.volume}|${t.receivedAt}`} t={t} prev={rows[i + 1]} fresh={t.source === 'live' && now - t.receivedAt < 1500} />
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

const TapeRow = ({ t, prev, fresh }: { t: LiveTrade; prev?: LiveTrade; fresh: boolean }) => {
  const dir = prev ? Math.sign(t.price - prev.price) : 0;
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '5px 20px', fontSize: 12.5, borderTop: '1px solid var(--tk-line-2)', background: fresh ? (dir < 0 ? 'var(--tk-down-soft)' : 'var(--tk-up-soft)') : 'transparent', transition: 'background 0.8s var(--tk-ease)', ...mono }}
    >
      <span style={{ color: 'var(--tk-ink-3)' }}>{t.time}</span>
      <span style={{ textAlign: 'right', fontWeight: 600, color: dir > 0 ? 'var(--tk-up)' : dir < 0 ? 'var(--tk-down)' : 'var(--tk-ink)' }}>{fmt(t.price)}</span>
      <span style={{ textAlign: 'right', color: 'var(--tk-ink-2)' }}>{fmtInt(t.volume)}</span>
    </div>
  );
};
